import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/sections";

export default function BrandStory() {
  return (
    <section
      id="story"
      data-testid="section-story"
      className="deck-section relative bg-ink"
    >
      <div className="mx-auto grid w-full max-w-[1600px] grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 px-6 md:px-12 lg:px-16 py-24 md:py-32">
        {/* Left — atelier / craftsmanship image */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Reveal>
            <div className="relative aspect-[5/4] md:aspect-[5/4] w-full overflow-hidden">
              <img
                src={IMAGES.atelier}
                alt="Aarya24KT atelier — the ritual of applying 24KT gold leaf"
                crossOrigin="anonymous"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-luxe hover:scale-[1.03]"
                style={{
                  /* Center crop keeps the hand, Ganesha plaque AND the Aarya24KT box in frame */
                  objectPosition: "50% 55%",
                  /* Light, neutral grade — preserves the new softer gold tone (no extra saturation) */
                  filter: "contrast(1.03) brightness(0.98)",
                }}
              />
              {/* Subtle warm gradient overlay */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(17,17,17,0.10) 0%, rgba(17,17,17,0) 35%, rgba(17,17,17,0.05) 60%, rgba(17,17,17,0.70) 100%)",
                }}
              />
              {/* Gold hairline frame + corner accents */}
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/30" />
              <div className="pointer-events-none absolute top-3 left-3 h-5 w-5 border-t border-l border-gold/60" />
              <div className="pointer-events-none absolute top-3 right-3 h-5 w-5 border-t border-r border-gold/60" />
              <div className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 border-b border-l border-gold/60" />
              <div className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 border-b border-r border-gold/60" />
              <div className="absolute bottom-5 left-5 z-10">
                <div className="num-overline text-gold/80">The Atelier</div>
                <div className="mt-1 font-heading italic text-ivory text-xl md:text-2xl">
                  Mumbai · Since 2008
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right — content */}
        <div className="lg:col-span-7 order-1 lg:order-2 flex flex-col">
          <Reveal>
            <div className="num-overline">02 · Brand Story</div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="mt-10 font-heading text-ivory text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tightest max-w-[14ch]">
              Gold has always travelled with{" "}
              <span className="italic text-gold">Indians.</span>
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 max-w-3xl">
            <Reveal delay={0.2}>
              <p className="font-body text-[15px] leading-[1.85] text-beige/85">
                Aarya24KT was founded in Mumbai with a simple belief — that gold
                is more than material. For Indian families across the world, it
                represents faith, memory, celebration and belonging.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="font-body text-[15px] leading-[1.85] text-beige/85">
                For over sixteen years, Aarya24KT has transformed these emotions
                into objects designed for modern living — partnering with 3500+
                retailers across India, and now expanding globally across the
                UK, USA, Australia and UAE.
              </p>
            </Reveal>
          </div>

          {/* Founder quote block with small aesthetic avatar */}
          <Reveal delay={0.45}>
            <div className="mt-16 border-l border-gold/40 pl-6 md:pl-8 max-w-2xl">
              <p className="font-heading italic text-2xl md:text-3xl leading-[1.35] text-ivory">
                “We wanted to create a gifting house where Indian culture could
                be experienced through luxury design.”
              </p>
              <div className="mt-6 flex items-center gap-4">
                <div className="relative h-16 w-16 overflow-hidden ring-1 ring-gold/40">
                  <img
                    src={IMAGES.founder}
                    alt="Kaivan Shah — Founder, Aarya24KT"
                    crossOrigin="anonymous"
                    className="absolute inset-0 h-full w-full object-cover object-top"
                    style={{
                      filter: "grayscale(0.2) contrast(1.05) sepia(0.08)",
                    }}
                  />
                </div>
                <div>
                  <div className="font-body text-sm text-ivory">— Kaivan Shah</div>
                  <div className="num-overline mt-1 text-ivory/55">
                    Founder · Aarya24KT
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
